import { type ReactElement, useMemo } from 'react';

import { formatSql } from '@/_shared/lib/sql';

import { CodeBlock } from './CodeBlock';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';

/**
 * A modal that shows a block of SQL — pretty-printed ({@link formatSql}) and syntax-highlighted, with a
 * copy button (both via {@link CodeBlock}). Shared by every "Show SQL" affordance (the whole-audience
 * compiled query and the raw-SQL predicate clause). The code region caps at a fraction of the viewport and
 * scrolls — the card header (language + copy) stays pinned — so a large query never grows the dialog.
 */
export function SqlModal({
  open,
  onOpenChange,
  sql,
  title = 'SQL',
  description,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sql: string;
  title?: string;
  description?: string;
}): ReactElement {
  const formatted = useMemo(() => formatSql(sql), [sql]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-3xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>
        {/* The code scrolls inside the card (maxHeight) so its header — language + copy — stays pinned. */}
        <CodeBlock content={formatted} metadata={{ language: 'sql' }} maxHeight="60vh" />
      </DialogContent>
    </Dialog>
  );
}
